<script lang="ts" setup>
import { Suggestion } from "~/components/Suggestions/Categories.props"
import { mapProductsAlgolia, ProductAlgolia } from "~/utils/mapProducts"
import { checkEnvSite } from "~~/server/utils/getAlgoliaConfig"
import { buildExcludeFilter, EAnalyticsTag } from "~/utils/algolia"
import type { WithContext, ItemList } from "schema-dts"
import { Hit, BaseHit } from "@algolia/client-search"
import { ListingType } from "~/utils/listing/listing"
import { AlgoliaListingPageProps } from "./ListingPage.props"

const router = useRouter()
const { pageInfos, filters, filtersSlug } = usePageInfo()

// Skeleton stays up for the whole transition (route change + search) so a
// stale/unfiltered listing is never flashed.
const { showSkeleton, markRouteChangeStart } = useListingLoadingState()

const haveSubCategories = ref(false)
const categories = ref()

const indexName: string = `TOP_PRODUCTS${checkEnvSite()}`

const indexCategoriesName = useAlgoliaSearch(`TOP_CATEGORIES${checkEnvSite()}`)
const isBrandListing = pageInfos?.value?.listingType === ListingType.brand
const isCategoryListing = pageInfos?.value?.listingType === ListingType.category
const hasListingTitle = !isBrandListing && !isCategoryListing

const { getAlgoliaAnalyticsTags } = useAlgoliaAnalytics()

const props = withDefaults(defineProps<AlgoliaListingPageProps>(), {
  hitsPerPage: 40
})

categories.value = filtersSlug.value
  ? await indexCategoriesName
      .search({
        query: "",
        requestOptions: {
          hitsPerPage: 50,
          distinct: true,
          filters: `parentCategory.slug:${filtersSlug.value}`,
          clickAnalytics: true,
          ...getAlgoliaAnalyticsTags()
        }
      })
      .then((response) => {
        haveSubCategories.value = response.hits.length > 0
        return response.hits
      })
  : null

const getSuggestionsCategoriesButtons = (items: any[]): Suggestion[] => {
  let orderedCategories = _orderBy(items, "slug").map(
    (item: any): Suggestion => ({
      text: item.categoryName,
      path: `c-${item.slug}` // TODO: path with accented chars in slug is not working
    })
  )
  return orderedCategories
}

const formatProducts = (items: ProductAlgolia[]) => {
  return mapProductsAlgolia(items)
}

const sortingItems = ref([
  {
    value: `TOP_PRODUCTS${checkEnvSite()}`,
    label: "Miglior risultato"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_discount-desc`,
    label: "Sconto decrescente"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_name-asc`,
    label: "Nome prodotto A-Z"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_name-desc`,
    label: "Nome prodotto Z-A"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_price-asc`,
    label: "Prezzo crescente"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_price-desc`,
    label: "Prezzo decrescente"
  }
])

const client = useAlgoliaRef()
const stateMapping = useStateMapping()
const algoliaRouter = useCustomRouting({ experience: "catalog" })

const configuration = ref({
  indexName: indexName,
  insights: true,
  routing: {
    router: algoliaRouter.value.router,
    stateMapping: stateMapping(indexName)
  },
  searchClient: client
})

// NOTE: `page` is intentionally NOT set here — pagination is owned by IS via
// the hidden AisPagination widget below, keeping configure params stable.
const configureSearchParameters = {
  query: pageInfos.value.query,
  hitsPerPage: props.hitsPerPage,
  distinct: true,
  clickAnalytics: true,
  filters: filters.value,
  ...getAlgoliaAnalyticsTags(),
  ...buildExcludeFilter({ excludeOutOfMarket: true })
}

const widgets = ref([
  useAisHits({
    // @ts-ignore
    transformItems: (items) => formatProducts(items)
  }),
  useAisConfigure({
    searchParameters: configureSearchParameters
  }),
  // Hidden pagination widget: renders nothing, lets IS own `page` state.
  // The visible pager stays the custom NavigationPaginator.
  useAisPagination({}),
  useAisSearchBox({}),
  useAisStats({}),
  useAisSortBy({
    items: sortingItems.value
  }),
  useAisClearRefinements({}, "all"),
  useAisClearRefinements(
    {
      includedAttributes: ["price.amount_float"]
    },
    "price.amount_float"
  ),
  useAisClearRefinements(
    {
      includedAttributes: ["brand.name"]
    },
    "brand.name"
  ),
  useAisClearRefinements(
    {
      includedAttributes: ["mainCategory.name"]
    },
    "mainCategory.name"
  ),
  useAisClearRefinements(
    {
      includedAttributes: ["inStock", "deductibleProduct"]
    },
    "availability"
  ),
  useAisRefinementList({
    attribute: "brand.name"
  }),
  useAisCurrentRefinements({}, "all"),
  useAisCurrentRefinements(
    {
      includedAttributes: ["brand.name"]
    },
    "brand.name"
  ),
  useAisCurrentRefinements(
    {
      includedAttributes: ["mainCategory.name"]
    },
    "mainCategory.name"
  ),
  useAisRangeInput({
    attribute: "price.amount_float"
  }),
  useAisRefinementList({
    attribute: "mainCategory.name"
  }),
  useAisToggleRefinement({
    attribute: "isDiscounted"
  }),
  useAisToggleRefinement({
    attribute: "inStock"
  }),
  useAisToggleRefinement({
    attribute: "deductibleProduct"
  })
])

const itemList: Ref<Hit<BaseHit>[] | null> = ref(null)

// Show skeleton on navigation start; cleared once the search settles.
// Unregister on unmount to avoid stacking guards across remounts.
const removeNavigationGuard = router.beforeEach((to) => {
  markRouteChangeStart(to.fullPath)
})
onBeforeUnmount(() => removeNavigationGuard())

useNuxtApp().hook("app:rendered", () => {
  if (import.meta.server && !!itemList.value) {
    const baseUrl = useRuntimeConfig().public.BASE_URL_APP
    const jsonLd: ComputedRef<WithContext<ItemList>> = computed(() => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      numberOfItems: itemList.value?.length,
      itemListElement: itemList.value?.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        image: item.productImage?.image?.url,
        url: baseUrl + item.path,
        name: item.title
      }))
    }))

    useHead({
      script: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd.value)
        }
      ]
    })
  }
})
</script>

<template>
  <div>
    <AisInstantSearch
      v-if="widgets && configuration"
      :widgets="widgets"
      :configuration="configuration"
      instance-key="products-listing"
    >
      <AlgoliaSearchStateTracker label="listing" />
      <div
        v-if="
          pageInfos?.listingType ===
          'category' /*&&  categories && categories.length > 0*/
        "
      >
        <SuggestionsCategories
          class="show-no-script"
          :title="`${pageInfos?.mainTitle ?? pageInfos?.categoryName}`"
          :info-text="`${pageInfos?.infoText ?? ''}`"
          :suggestions="getSuggestionsCategoriesButtons(categories)"
        />
      </div>
      <slot name="content" />
      <div class="grid-standard mt-4 md:pt-6" id="algolia-plp-filters">
        <p
          v-if="hasListingTitle"
          class="elephant-bold col-span-full hidden md:mb-6 md:block"
        >
          {{ formatNameWithoutBackSlash(pageInfos?.pageTitle!) }}
        </p>

        <AlgoliaPlpFilters
          :show-query="pageInfos?.listingType !== 'brandCategory'"
          :hasResultsNumberUnderFilters="isBrandListing || isCategoryListing"
        />
      </div>

      <AisHits>
        <template #default="{ items, searchMetadata }">
          <SkeletonListingPage
            v-if="searchMetadata?.isSearchStalled || showSkeleton"
          />
          <template v-else>
            {{ (itemList = items) && "" }}
            <div v-if="items.length === 0">
              <AlgoliaNoResults />
            </div>
            <div v-else>
              <slot name="products" :items="items" :nbHits="0" />
            </div>
          </template>
        </template>
      </AisHits>
    </AisInstantSearch>
  </div>
</template>
